import {
  Address,
  ChainPublicClientService,
  ProdProtocolKeyService,
  ProtocolKeyService,
  isProdProtocolKey,
} from "@silo/types";
import { Array, Effect, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "../contracts-client/types";
import { UserMarketAddresses } from "../types";
import { P, match } from "ts-pattern";
import { GraphClient } from "../graph-client";
import { siloLensAbi } from "@silo/contract-abi-types";
import { siloLensAddress } from "@silo/contract-addresses";
import { satisfies } from "effect/Function";
import { BlobClient } from "../blob-client";

export type GetUserMarketAddresses = ReturnType<typeof getUserMarketAddresses>;
export type GetUserMarketAddressesSuccess =
  Effect.Effect.Success<GetUserMarketAddresses>;
export type GetUserMarketAddressesError =
  Effect.Effect.Error<GetUserMarketAddresses>;
export type GetUserMarketAddressesContext =
  Effect.Effect.Context<GetUserMarketAddresses>;

export function getUserMarketAddresses(user: Address) {
  return Effect.gen(function* () {
    yield* Effect.logInfo("Begin 🎬: get user market addresses");

    const protocolKey = yield* ProtocolKeyService;

    const { source, marketAddresses } = yield* match(protocolKey)
      .when(isProdProtocolKey, (prodProtocolKey) =>
        Effect.firstSuccessOf([
          pipe(
            GraphClient.getUserMarketAddresses(user),
            Effect.provideService(ProdProtocolKeyService, prodProtocolKey)
          ),
          getStaticUserMarketAddresses(user),
        ])
      )
      .with(P.union("local", "anvil"), () => getStaticUserMarketAddresses(user))
      .exhaustive();

    yield* Effect.logInfo(
      `Complete ✅: get user market addresses from ${source} (count: ${marketAddresses.length})`
    );

    return marketAddresses;
  });
}

export type GetStaticUserMarketAddresses = ReturnType<
  typeof getStaticUserMarketAddresses
>;
export type GetStaticUserMarketAddressesSuccess =
  Effect.Effect.Success<GetStaticUserMarketAddresses>;
export type GetStaticUserMarketAddressesError =
  Effect.Effect.Error<GetStaticUserMarketAddresses>;
export type GetStaticUserMarketAddressesContext =
  Effect.Effect.Context<GetStaticUserMarketAddresses>;

export function getStaticUserMarketAddresses(user: Address) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const staticMarkets = yield* BlobClient.getProtocolStaticMarkets(
      protocolKey
    );

    const allMarketAddresses = pipe(
      staticMarkets,
      Array.map((market) => market.marketAddress)
    );

    const { publicClient } = yield* ChainPublicClientService;

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const positionData = yield* Effect.tryPromise({
      try: () => {
        return Promise.all(
          allMarketAddresses.map(async (marketAddress) => {
            const hasPosition = await siloLensContract.read.hasPosition([
              marketAddress,
              user,
            ]);

            return { hasPosition, marketAddress };
          })
        );
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const marketAddresses = positionData.flatMap(
      ({ hasPosition, marketAddress }) => (hasPosition ? [marketAddress] : [])
    );

    const userMarketAddresses = satisfies<UserMarketAddresses>()({
      source: "static",
      marketAddresses,
    } as const);

    return userMarketAddresses;
  });
}
